import React, { useState, useCallback, useContext, useMemo } from 'react';
import { graphql } from 'react-relay';
import { defineMessages, useIntl } from 'react-intl-next';
import { get as levenshteinDistance } from 'fast-levenshtein';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { getPageElementCounts } from '@atlaskit/editor-common/utils';
import type { EditorActions } from '@atlaskit/editor-core';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';
import type { SyncedBlockPlugin } from '@atlaskit/editor-plugin-synced-block';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { useReactProps_DO_NOT_USE } from '@atlassian/embedded-confluence/useReactProps';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import {
	EDIT_PAGE_PUBLISH_EXPERIENCE,
	MIGRATE_PAGE_PUBLISH_EXPERIENCE,
	AUTOMATION_DISCOVERY_PUBLISH_MEETING_NOTES_EXPERIENCE,
	AUTOMATION_DISCOVERY_JIRA_LINK,
	LIVE_PAGE_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { stopExperienceOnError } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { usePageConflictAnalytics } from '@confluence/editor-flags/entry-points/usePageConflictAnalytics.ts';
import { useEditorFlagsContext } from '@confluence/editor-flags/entry-points/EditorFlagsContext.ts';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants.ts';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useDraftSyncAndPublishContext } from '@confluence/native-collab/entry-points/useDraftSyncAndPublishContext.ts';
import type { ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import {
	getEditorOnboardingCache,
	getCachedIsOnboardingEditorLabel,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useOnboardingEditorActions } from '@confluence/onboarding-editor/entry-points/useOnboardingEditorState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useEditorModalContext, ALLOWED_EDITOR_MODAL } from '@confluence/editor-modal';
import { useCopyContentLink } from '@confluence/share/entry-points/useCopyContentLink.ts';
import { PAGE_MODE } from '@confluence/share/entry-points/CopyLink.tsx';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import type { RestrictionsRESTObjForContentApi } from '@confluence/restrictions/entry-points/RestrictionsREST.ts';
import type { GqlRestrictionsInput } from '@confluence/restrictions/entry-points/convertRestrictionsToGqlRestrictionsInput.ts';
import { LazyWhiteBoardPublishDiscoveryDialog } from '@confluence/automation-discovery/entry-points/WhiteBoardPublishDiscoveryDialog.ts';
import { LazyJiraLinkAutomationDiscoveryDialog } from '@confluence/automation-discovery/entry-points/JiraLinkAutomationDiscoveryDialog.ts';
import {
	LazyReleaseNotesPublishDiscoveryDialog,
	isEligibleForReleaseNotesDiscovery,
} from '@confluence/automation-discovery/entry-points/ReleaseNotesDialog.ts';
import {
	LazyFeedbackAnalysisDiscoveryDialog,
	isEligibleForFeedbackAnalysisDiscovery,
} from '@confluence/automation-discovery/entry-points/FeedbackAnalysisDialog.ts';
import {
	LazyProductRequirementsPublishDiscoveryDialog,
	isEligibleForProductRequirementsDiscovery,
} from '@confluence/automation-discovery/entry-points/ProductRequirementsDialog.ts';
import { isEligibleForWhiteboardDiscovery } from '@confluence/automation-discovery/entry-points/whiteboard-support.ts';
import { doesADFContainJiraNodes } from '@confluence/automation-discovery/entry-points/jira-detection.ts';
import { LazyPagePublishAutomationDiscoveryDialog } from '@confluence/automation-discovery/entry-points/PagePublishAutomationDiscoveryDialog.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import {
	useInviteOnLinkInsertEligibilityCheck,
	useShowInviteOnLinkInsertFlag,
} from '@confluence/invite-on-link-insert/entry-points/useInviteOnLinkInsertExperiment.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { containsNetworkError } from '@confluence/network';
import {
	WatchDialogComponentWithWatchChildrenQuery,
	type WatchDialogComponentWithWatchChildrenQueryType,
	type WatchDialogComponentWithWatchChildrenQueryVariables,
} from '@confluence/watch-dialog/entry-points/WatchDialogComponentWithWatchChildrenQuery.ts';
import { useGeneratedTitle } from '@confluence/title-area-utils/entry-points/useGeneratedTitle.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { showPublishSuccessFlagsWithSocialAndCopyLink } from '../../utils/showPublishSuccessFlagsWithSocialAndCopyLink.tsx';
import { useShareLinkSocialOnPublish } from './useShareLinkSocialOnPublish.ts';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { PageRestrictionsContextQuery } from '@confluence/page-restrictions-context/entry-points/PageRestrictionsContextQuery.graphql.ts';
import { useAutoConvertState } from '@confluence/editor-conversion/entry-points/useAutoConvertState.ts';

import { useForceRefetchContent } from '../useForceRefetchContent.ts';
import { useEditorAnalyticsEvent } from '../useEditorAnalyticsEvent.ts';
import { useMigratedRecently } from '../useMigratedRecently/index.ts';
import { useGetCollabProviderState } from '../useGetCollabProviderState.ts';
import { usePublishCompanyHub } from '../usePublishCompanyHub/index.ts';
import { showPublishSuccessFlags } from '../../utils/showPublishSuccessFlags.tsx';
import { FABRIC_PUBLISH_PAGE_METRIC } from '../../perf.config.ts';
import { getBlueprintFromLabels } from '../useBlueprintType/useBlueprintType.ts';
import { showFlushSyncBlockErrorFlag } from '../../utils/showSyncBlockErrorFlag.tsx';

import { useForceRefetchPublishedLiveDoc } from './useForceRefetchPublishedLiveDoc.tsx';
import { useExperimentalPublishEditorContentBlueprintMutation } from './useExperimentalPublishEditorContentBlueprintMutation.ts';
import type { PublishMutation_experimentalPublishEditorContent } from './__types__/PublishMutation.ts';
import type {
	usePublishUpdatePageMutation$data,
	usePublishUpdatePageMutation$variables,
} from './__generated__/usePublishUpdatePageMutation.graphql.ts';
import type {
	usePublishUpdateBlogMutation$data,
	usePublishUpdateBlogMutation$variables,
} from './__generated__/usePublishUpdateBlogMutation.graphql.ts';

export type UsePublishProps = {
	editorActions: EditorActions;
	contentId: string;
	contentType: string;
	embeddedConfluenceSource?: string;
	refetchNativeCollab: (shouldCheckCache: boolean) => Promise<any>;
	spaceKey: string;
	afterPublish?: (args: {
		res: PublishMutation_experimentalPublishEditorContent | null | undefined;
		newSpaceKey: string | undefined;
		isLivePagePublish: boolean;
	}) => void; // Custom callback invoked after successful publish
	version: number;
	onCollabError: () => void;
	saveDraftPromise: Promise<any> | null;
	checkIfConditionPreventingPublish?: () => Promise<boolean>;
	flags?: FlagsStateContainer;
	dialogs?: DialogsStateContainer;
	startingLinks?: Set<string>;
	optimisticDeleteItemInTree?: (contentId: string) => void;
	editorPresetAPI?: PublicPluginAPI<[CollabEditPlugin, SyncedBlockPlugin]> | null;
};

export type PublishProps = {
	suppressNotifyWatchers?: boolean;
	message?: string;
	schedulePublishDate?: string | null;
	ancestors?: { id: string }[];
	newSpaceKey?: string;
	moveRequest?: {
		targetId?: string;
		position?: string;
		previousParentPageId?: string;
	};
	// When 'use_graphql_to_publish_pages_and_blogs' is enabled, restrictions should be GqlRestrictionsInput.
	// Otherwise, it should be RestrictionsRESTObjForContentApi.
	restrictions?: GqlRestrictionsInput | RestrictionsRESTObjForContentApi;
	retryOnConflict?: boolean;
};

type HandlePublishProps = PublishProps & {
	isButtonClicked?: boolean;
};
const MAX_PUBLISH_HANG_TIME = 15000;

const i18n = defineMessages({
	titleKeywordForAutomationDiscovery: {
		id: 'editor-features.title-keyword-for-automation-discovery.',
		defaultMessage: 'meeting',
		description: 'Keyword in title that triggers automation discovery dialog',
	},
	networkErrorTitle: {
		id: 'editor-features.use-publish.network-error.title',
		defaultMessage: 'Can’t save changes',
		description:
			'Title of flag notifying that the user appears to have had network issues while publishing',
	},
	networkErrorDescription: {
		id: 'editor-features.use-publish.network-error.description',
		defaultMessage:
			'Check your network connection, refresh the page, and try again. If the problem persists, contact your administrator for help.',
		description:
			'Contents of flag notifying that the user appears to have had network issues while publishing',
	},
	outdatedRefetchErrorFlagTitle: {
		id: 'editor-features.use-publish.outdated-refetch-error-flag.title',
		defaultMessage: 'To view your published page, refresh now',
		description:
			'Title of flag notifying that the user to refresh due to server issues right after publishing',
	},
	outdatedRefetchErrorFlagDescription: {
		id: 'editor-features.use-publish.outdated-refetch-error-flag.description',
		defaultMessage:
			'{pageTitle} was converted and published, but we weren’t able to redirect to the converted page. If this keeps happening, contact Support.',
		description:
			'Contents of flag notifying that the user to refresh due to server issues right after publishing',
	},
	outdatedRefetchFlagRefresh: {
		id: 'editor-features.use-publish.outdated-refetch-error-flag.refresh',
		defaultMessage: 'Refresh',
		description:
			'Refresh link at bottom of flag notifying that the user to refresh due to server issues right after publishing',
	},
	isAutoConvertedVersionMessage: {
		id: 'editor-features.use-publish.is-auto-converted-version-message',
		defaultMessage: 'Version published after converting to the new editor',
		description: 'Message to show that the page has been converted to the new editing experience',
	},
});

export class CollabPublishError extends Error {
	constructor(
		public message: string,
		public traceId?: string | null,
	) {
		super(message);
		this.traceId = traceId;
	}
}

export const usePublish = ({
	contentId,
	contentType,
	refetchNativeCollab,
	editorActions,
	embeddedConfluenceSource,
	spaceKey,
	afterPublish,
	version,
	onCollabError,
	saveDraftPromise,
	checkIfConditionPreventingPublish,
	flags,
	dialogs,
	startingLinks,
	optimisticDeleteItemInTree,
	editorPresetAPI,
}: UsePublishProps): {
	onPublish: ({
		suppressNotifyWatchers,
		message,
		schedulePublishDate,
		ancestors,
		restrictions,
		retryOnConflict,
		newSpaceKey,
		moveRequest,
	}: PublishProps) => () => Promise<void>;
	isPublishingContent: boolean;
} => {
	const intl = useIntl();
	const [isPublishingContent, setIsPublishingContent] = useState<boolean>(false);
	const { showEditorFlag } = useEditorFlagsContext();
	const { showEditorModal } = useEditorModalContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, isLoggedIn, isLicensed, edition, isAdminHubAIEnabled, userId } =
		useSessionData();
	const [isAutoConverted] = useAutoConvertState();
	const includeAncestors = ConfluenceEdition.FREE === edition;

	const publishMutationOptions = {
		refetchQueries: [
			{
				query: PageRestrictionsContextQuery,
				variables: { contentId, status: ['current', 'draft', 'archived'], includeAncestors },
			},
		],
		update: () => {
			const contentData = readQueryInterop<
				WatchDialogComponentWithWatchChildrenQueryType,
				WatchDialogComponentWithWatchChildrenQueryVariables
			>({
				query: WatchDialogComponentWithWatchChildrenQuery,
				variables: { contentId },
			});

			const updatedData = JSON.parse(JSON.stringify(contentData));

			if (!updatedData || !updatedData.singleContent) return;

			const { singleContent } = updatedData;
			const { space } = singleContent;

			if (contentType === 'page') {
				singleContent.currentUserIsWatching = true;
			} else if (space) {
				if (contentType === 'space') {
					space.currentUser.isWatched = true;
				} else if (contentType === 'blogpost') {
					space.currentUser.isWatchingBlogs = true;
				}
			}

			writeQueryInterop<
				WatchDialogComponentWithWatchChildrenQueryType,
				WatchDialogComponentWithWatchChildrenQueryVariables
			>({
				query: WatchDialogComponentWithWatchChildrenQuery,
				data: updatedData,
			});
		},
	};

	const [publishPageMutation] = useMutationInterop<
		usePublishUpdatePageMutation$data,
		usePublishUpdatePageMutation$variables
	>(
		graphql`
			mutation usePublishUpdatePageMutation(
				$contentId: ID!
				$content: String!
				$message: String
				$spaceKey: String!
				$suppressNotifyWatchers: Boolean
				$ncsStepVersion: String!
				$title: String!
				$version: Int!
				$schedulePublishDate: String
				$moveRequest: ConfluenceMoveRequestInput
				$restrictions: PageRestrictionsInput
				$cloudId: ID!
				$restrictionState: ConfluenceContentRestrictionStateInput
				$permissions: ConfluenceContentPermissionsInput
			) {
				confluence_updatePage(
					cloudId: $cloudId
					input: {
						confluenceContentInput: {
							contentBody: $content
							contentId: $contentId
							minorEdit: $suppressNotifyWatchers
							moveRequest: $moveRequest
							ncsStepVersion: $ncsStepVersion
							restrictions: $restrictions
							schedulePublishDate: $schedulePublishDate
							spaceKey: $spaceKey
							title: $title
							version: $version
							versionMessage: $message
							restrictionState: $restrictionState
							permissions: $permissions
						}
						status: "draft"
					}
				) {
					success
					content {
						status
						space {
							name
						}
					}
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		`,
		publishMutationOptions,
	);

	const [publishBlogMutation] = useMutationInterop<
		usePublishUpdateBlogMutation$data,
		usePublishUpdateBlogMutation$variables
	>(
		graphql`
			mutation usePublishUpdateBlogMutation(
				$contentId: ID!
				$content: String!
				$message: String
				$spaceKey: String!
				$suppressNotifyWatchers: Boolean
				$ncsStepVersion: String!
				$title: String!
				$version: Int!
				$schedulePublishDate: String
				$moveRequest: ConfluenceMoveRequestInput
				$restrictions: PageRestrictionsInput
				$cloudId: ID!
				$restrictionState: ConfluenceContentRestrictionStateInput
				$permissions: ConfluenceContentPermissionsInput
			) {
				confluence_updateBlogPost(
					cloudId: $cloudId
					input: {
						confluenceContentInput: {
							contentBody: $content
							contentId: $contentId
							minorEdit: $suppressNotifyWatchers
							moveRequest: $moveRequest
							ncsStepVersion: $ncsStepVersion
							restrictions: $restrictions
							schedulePublishDate: $schedulePublishDate
							spaceKey: $spaceKey
							title: $title
							version: $version
							versionMessage: $message
							restrictionState: $restrictionState
							permissions: $permissions
						}
						status: "draft"
					}
				) {
					success
					content {
						status
						space {
							name
						}
					}
					errors {
						message
						extensions {
							statusCode
						}
					}
				}
			}
		`,
		publishMutationOptions,
	);

	const [experimentalPublishEditorContentBlueprintMutation] =
		useExperimentalPublishEditorContentBlueprintMutation();

	const [{ collabEditProvider, collabEditInitialized }] = useNativeCollabState();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const forceRefetchContent = useForceRefetchContent(contentId, spaceKey);
	const forceRefetchPublishedLiveDoc = useForceRefetchPublishedLiveDoc({
		forceRefetchContent,
		maxRetryCount: 1,
		retryDelayMs: 300,
	});

	const { copyContentLink } = useCopyContentLink({
		pageMode: PAGE_MODE.VIEW,
		contentType,
		contentId,
		spaceKey,
		source: 'usePublish - publishSuccessFlag',
	});

	const migratedRecently = useMigratedRecently(contentId);
	const { setIsPublishing } = useDraftSyncAndPublishContext();
	const embeddedOnPublish = useReactProps_DO_NOT_USE();
	const [state, action] = useGeneratedTitle();
	const isLivePage = useIsCurrentPageLive();
	const readLabelsFragment = useReadFragmentInterop({
		fragmentSpec: graphql`
			fragment usePublishReadLabelsFragment on Content {
				labels {
					nodes {
						id
						label
					}
				}
			}
		`,
	});

	const readEditorOnboardingStateFragment = useReadFragmentInterop({
		fragmentSpec: graphql`
			fragment usePublishReadEditorOnboardingStateFragment on OnboardingState {
				key
				value
			}
		`,
	});

	/* Onboarding Feature */
	const { setOnboardingState } = useSetOnboardingState();
	const { editorOnboardingEligiblity, hasUserPublished, editorTutorialSuccessFlagCompleted } =
		isLoggedIn && isLicensed
			? getEditorOnboardingCache(readEditorOnboardingStateFragment)
			: {
					editorOnboardingEligiblity: null,
					hasUserPublished: true,
					editorTutorialSuccessFlagCompleted: true,
				};
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	// Hub editor does not support editor labels
	const isPublishingOriginalOnboardingTemplate =
		isLoggedIn && isLicensed && !isCompanyHub
			? getCachedIsOnboardingEditorLabel(contentId, readLabelsFragment)
			: false;
	const { setHasMetPublishNudgeConditions } = useOnboardingEditorActions();
	const getCollabProviderState = useGetCollabProviderState({
		editorActions,
		collabEditProvider,
		reason: 'publish',
	});

	const { triggerAnalyticsEvent } = useEditorAnalyticsEvent({
		contentId,
		contentType,
		embeddedConfluenceSource,
		source: 'editPageScreen',
		subject: 'page',
		version,
		collabService: 'ncs',
	});

	const { triggerPageConflictAnalytics } = usePageConflictAnalytics({
		source: 'usePublish',
		actionSubjectId: 'editor.publish',
		contentId,
		contentType,
		collabService: 'ncs',
		version,
	});

	const percentDifference = (s1: string, s2: string): number => {
		const maxLength = Math.max(s1.length, s2.length);
		if (maxLength === 0) {
			return 0;
		}
		const distance = levenshteinDistance(s1, s2);

		return 100 - ((maxLength - distance) / maxLength) * 100;
	};

	usePublishCompanyHub(spaceKey, contentId, flags, refetchNativeCollab);

	const inviteOnLinkInsertEligibilityCheck = useInviteOnLinkInsertEligibilityCheck(flags);

	const showInviteOnLinkInsertFlag = useShowInviteOnLinkInsertFlag(flags);

	const {
		isEligible: isEligibleShareLinkSocialOnPublish,
		suggestedUsers,
		showShareModal,
	} = useShareLinkSocialOnPublish(contentId, spaceKey, userId);

	const handlePublish = useCallback(
		async ({
			suppressNotifyWatchers,
			message,
			retryOnConflict,
			isButtonClicked,
			schedulePublishDate,
			ancestors,
			newSpaceKey,
			restrictions,
			moveRequest,
		}: HandlePublishProps) => {
			// Checking condition only when button clicked (skip on retry)
			if (
				isButtonClicked &&
				checkIfConditionPreventingPublish &&
				(await checkIfConditionPreventingPublish())
			) {
				return;
			}

			if (isPublishingContent) {
				// Short circuit to prevent race conditions when multiple publishing calls are fired in rapid succession.
				return;
			}

			// Live docs -> published page conversion utilizes this hook and we want to separate
			// conversion experience tracking from regular page publish experience tracking
			const experienceTrackingKey = isLivePage
				? LIVE_PAGE_PUBLISH_EXPERIENCE
				: EDIT_PAGE_PUBLISH_EXPERIENCE;
			const experienceTrackingTimeout = isLivePage
				? ExperienceTimeout.LIVE_PAGE_PUBLISH
				: ExperienceTimeout.EDITOR_PUBLISH;

			if (isButtonClicked) {
				experienceTracker.start({
					name: experienceTrackingKey,
					timeout: experienceTrackingTimeout,
					attributes: {
						collabService: 'ncs',
						isCompanyHub,
						contentId,
					},
				});
				FABRIC_PUBLISH_PAGE_METRIC.start();
			}

			// Start the publish timeout
			const publishTimeout = setTimeout(() => {
				showEditorFlag({
					errorName: ALLOWED_FLAG_MESSAGES.PUBLISH_TIME_TOO_LONG_ERROR,
					isUnpublishedDraft,
				});
			}, MAX_PUBLISH_HANG_TIME);

			// TODO: add experience tracking and editor flag timeout
			setIsPublishingContent(true);

			if (saveDraftPromise) {
				try {
					await saveDraftPromise;
				} catch {
					// no-op
				}
			}

			setIsPublishing(true);
			const data = await refetchNativeCollab(false);
			const blueprintType = getBlueprintFromLabels(data);
			const content = data?.content?.nodes?.[0];
			const isUnpublishedDraft = !content?.history?.createdBy;
			const versionFromQuery = content?.version?.number ?? 1;
			const currentSpaceName = content?.space?.name;
			const subType = content?.subType;
			const isSpaceOverview = content?.space?.homepageId === contentId ? true : undefined;

			const defaultErrorAttributes: ExperienceAttributes = {
				blueprintType,
				isUnpublishedDraft,
				contentId,
				retryOnConflict,
				collabEditInitialized,
				collabService: 'ncs',
				isPublishing: isPublishingContent,
				migratedRecently,
			};

			let isPublishLiveDocRedirectFailed = false;
			void Promise.resolve(getCollabProviderState())
				.then((ackState) => {
					if (!ackState) {
						experienceTracker.fail({
							name: experienceTrackingKey,
							error: new Error(`${experienceTrackingKey} failed: ackState is undefined`),
							attributes: defaultErrorAttributes,
						});
						return;
					}
					const { title, content, stepVersion } = ackState;
					const newVersionNumber = isUnpublishedDraft ? 1 : versionFromQuery + 1;

					// show onboarding modal that prompts user to enter the title when title is empty
					if (!title && editorOnboardingEligiblity && fg('cc_onboarding_experience')) {
						// only show on normal publish (do not show modal for publish without notifying watchers OR publish with version comment)
						if (message === '' && !suppressNotifyWatchers) {
							void showEditorModal(ALLOWED_EDITOR_MODAL.ONBOARDING_EDITOR_MODAL);
							experienceTracker.abort({
								name: experienceTrackingKey,
								reason: `${experienceTrackingKey} aborted: title is empty on normal publish`,
								attributes: defaultErrorAttributes,
							});
							return;
						}
					}

					// Safety check, as title must be not empty for publish button to be enabled
					if (!title) {
						experienceTracker.abort({
							name: experienceTrackingKey,
							reason: `${experienceTrackingKey} aborted: title is empty`,
							attributes: defaultErrorAttributes,
						});
						showEditorFlag({
							errorName: ALLOWED_FLAG_MESSAGES.TITLE_REQUIRED,
							isUnpublishedDraft,
						});
						return;
					}

					// Is this a newly created blueprint page
					const isBlueprintCreate = !!blueprintType && isUnpublishedDraft;

					let publishMutation;
					if (isBlueprintCreate) {
						publishMutation = experimentalPublishEditorContentBlueprintMutation;
					} else {
						if (contentType === 'blogpost') {
							publishMutation = publishBlogMutation;
						} else {
							publishMutation = publishPageMutation;
						}
					}

					const processedRestrictions = restrictions;

					// Extract new permissions fields from GqlRestrictionsInput when using the
					// persistence_specific_access_gate experiment (new explicit permissions paradigm)
					const gqlInput = expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
						? (restrictions as GqlRestrictionsInput)
						: undefined;
					const permissionsInput = gqlInput?.permissions ?? undefined;
					const restrictionStateInput = gqlInput?.restrictionState ?? undefined;

					// When using new permissions paradigm, don't send legacy restrictions
					const legacyRestrictions =
						permissionsInput || restrictionStateInput ? undefined : processedRestrictions;

					// append text to message if isAutoConverted is true informing that this version was published after converting to the new editor
					if (
						isAutoConverted &&
						expValEquals('confluence_frontend_version_msg_autoconverted_page', 'isEnabled', true)
					) {
						if (message && message.trim()) {
							message = `${message} - ${intl.formatMessage(i18n.isAutoConvertedVersionMessage)}`;
						} else {
							message = intl.formatMessage(i18n.isAutoConvertedVersionMessage);
						}
					}

					return publishMutation({
						variables: {
							contentId,
							contentType,
							content: JSON.stringify(content),
							message,
							spaceKey: newSpaceKey || spaceKey,
							ncsStepVersion: stepVersion.toString(),
							suppressNotifyWatchers,
							title: title as string,
							version: newVersionNumber,
							schedulePublishDate,
							// @ts-ignore
							ancestors,
							// @ts-ignore
							restrictions: legacyRestrictions,
							permissions: permissionsInput,
							restrictionState: restrictionStateInput,
							// @ts-ignore
							moveRequest:
								moveRequest?.position && moveRequest?.targetId
									? {
											position: moveRequest?.position?.toUpperCase(),
											targetId: moveRequest?.targetId,
										}
									: undefined,
							isBlueprintCreate,
							cloudId,
						},
					}).then(async (response) => {
						// Retry once on a conflict when publishing
						let res;
						if (isBlueprintCreate) {
							res = response?.data?.experimentalPublishEditorContent;
						} else {
							if (contentType === 'blogpost') {
								res = response?.data?.confluence_updateBlogPost;
							} else {
								res = response?.data?.confluence_updatePage;
							}
						}
						const success = res?.success;
						let status;
						if (isBlueprintCreate) {
							status = res?.status;
						} else {
							status = res?.errors?.[0]?.extensions?.[0]?.statusCode;
						}
						let responseMessage;
						if (isBlueprintCreate) {
							responseMessage = res?.message;
						} else {
							responseMessage = res?.errors?.[0]?.message;
						}
						let traceId;
						if (isBlueprintCreate) {
							traceId = res?.traceId;
						} else {
							traceId = response?.extensions?.gateway?.trace_id;
						}
						let spaceName;
						if (isBlueprintCreate) {
							spaceName = res?.space?.name ?? currentSpaceName;
						} else {
							spaceName = res?.content?.space?.name ?? currentSpaceName;
						}
						if (status === 409 && retryOnConflict) {
							triggerPageConflictAnalytics(responseMessage, {
								cloudId,
								retry: retryOnConflict,
								newVersionNumber,
								migratedRecently,
								isUnpublishedDraft,
							});

							setIsPublishingContent(false);
							void handlePublish({
								suppressNotifyWatchers,
								message,
								retryOnConflict: false,
								isButtonClicked: false,
								schedulePublishDate,
								ancestors,
								newSpaceKey,
								restrictions,
							});
							// Short circuit so the experience doesn't get preemptively recorded as a failure.
							// Let the retry determine what happens to the experience.
							return;
						}
						clearTimeout(publishTimeout); // clear timeout earlier to not have false positives due to delay from after publish actions (such as fetching view content)

						if (success) {
							FABRIC_PUBLISH_PAGE_METRIC.stop({
								customData: {
									collabService: 'ncs',
									didRetry: !retryOnConflict,
								},
							});

							let flushSyncBlockSuccess: boolean | undefined;
							if (expValEquals('platform_synced_block', 'isEnabled', true)) {
								flushSyncBlockSuccess =
									await editorPresetAPI?.syncedBlock?.actions.flushBodiedSyncBlocks();

								editorPresetAPI?.syncedBlock?.actions.flushSyncedBlocks();
							}

							if (moveRequest?.previousParentPageId && optimisticDeleteItemInTree) {
								optimisticDeleteItemInTree(contentId); //delete the current page so it can be moved without duplication
							}

							// Fire analytics Track events on publish
							const publishAnalyticsAction = isUnpublishedDraft ? 'created' : 'updated';

							const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();

							if (state?.generatedTitle !== '') {
								const titleDiff = percentDifference(title, state.generatedTitle);
								createAnalyticsEvent({
									type: 'sendTrackEvent',
									data: {
										actionSubject: 'editorTitle',
										action: 'publishedAfterTitleGen',
										source: 'editPageScreen',
										attributes: {
											pageAction: publishAnalyticsAction,
											contentId,
											diffPercentage: titleDiff,
										},
									},
								}).fire();

								if (titleDiff < 50) {
									state.trackAIResultViewStored({ doesNotMeetMAUCriteria: false });
								}

								action.setGeneratedTitle('');
							}

							// Fire analytics for title generation on paste (standard pages only)
							if (state?.generatedTitleFromPaste !== '') {
								state.fireResultAnalyticsFromPaste();
								action.clearGeneratedTitleFromPaste();
							}

							const pageElementCounts = getPageElementCounts(content);

							const numTitleWords = title.match(/\b\w+\b/g);

							if (isEmbeddedConfluence) {
								/*
									When a user publishes an embedded page, a full page reload from edit -> view happens.
									We fire the edit-page/publish experience tracker event on publish success which the EP iframe listens for in order to reload.
									The firing of page create / page update happens asynchronously, so it can arrive much later than the experience tracker event.
									However, for the page relaod to happen, the EP iframe only listens on the experience tracker event, and once it is recieved,
									the edit iframe is unmounted and view iframe is mounted. During this unmount / re-mount, the track events are being lost.
									So we await on the async call to fire the analytics, and only after that do we send the experience tracker events.
								*/
								try {
									await triggerAnalyticsEvent(publishAnalyticsAction, {
										editorType: 'next',
										isLivePage: subType === 'live',
										pageElementCounts,
										titleLength: numTitleWords ? numTitleWords.length : 0,
									});
								} catch (error) {
									throw new Error(
										'An error occurred while firing Track analytics events on publish:',
										error.message,
									);
								}

								// Callback for passing page information to the parent product
								embeddedOnPublish?.onPublish?.(spaceName, newSpaceKey);
							} else {
								/*
									For Standalone Confluence, the track events fire consistently, so we don't need to await on them.
									Also, awaiting has caused some delays with publish in the past, so we avoid it.
									(Refer for context: https://product-fabric.atlassian.net/browse/PGXT-3858)
								*/
								void triggerAnalyticsEvent(publishAnalyticsAction, {
									editorType: 'next',
									isLivePage: subType === 'live',
									isFabric: true,
									pageElementCounts,
									titleLength: numTitleWords ? numTitleWords.length : 0,
									isSpaceOverview,
								});
							}

							// Dispatch experience tracker events after Track events queued to ensure firing events are in right sequence.
							// We're intentionally not succeeding the LIVE_PAGE_PUBLISH_EXPERIENCE here as it is succeeded later in ViewPageCommon
							// once the page conversion is successful and the user is directed to view page
							experienceTracker.succeed({
								name: EDIT_PAGE_PUBLISH_EXPERIENCE,
								attributes: {
									contentId,
									collabService: 'ncs',
									traceId,
									newSpaceKey, // SpaceKey is no longer considered UGC. This is used for CBT-3249 and will be removed when embedded confluence switches to PubSub
								},
							});

							migratedRecently &&
								experienceTracker.succeed({
									name: MIGRATE_PAGE_PUBLISH_EXPERIENCE,
									attributes: {
										contentId,
										collabService: 'ncs',
										traceId,
									},
								});

							if (!schedulePublishDate && afterPublish) {
								if (subType === 'live') {
									// For live docs, this logic is responsible for ensuring the user gets directed from the live doc to the view page.
									// This works because calling forceRefetchContent via forceRefetchPublishedLiveDoc has a GQL query retrieving the subType.
									// When the now-published subType `null` is returned by GQL and stored in Apollo cache, ViewPageCommon is able to re-render as a view page.
									// Very rarely the subType returns 'live' because GQL is outdated and doesn't see the page has been published, so
									//   forceRefetchPublishedLiveDoc retries to give more time to get the up-to-date subtype from GQL, reducing the likelihood this issue impacts the user.
									const { retryCount, isResponseOutdated, isResponseInvalid } =
										await forceRefetchPublishedLiveDoc();
									if (retryCount > 0) {
										// We want to track when we have to retry, to keep an eye on whether this retry fix resolves the outdated subtype bug. For more details see the comment above useForceRefetchPublishedLiveDoc.
										createAnalyticsEvent({
											type: 'sendOperationalEvent',
											data: {
												action: 'retried',
												actionSubject: 'forceRefetchPublishedLiveDoc',
												source: 'usePublish',
												attributes: {
													retryCount,
													isResponseOutdated,
													isResponseInvalid,
													contentId,
												},
											},
										}).fire();
									}
									if (isResponseOutdated || isResponseInvalid) {
										// If response is still improper after forceRefetchPublishedLiveDoc retries the query, it's likely the user isn't being directed to the view page, so show a flag recommending the user refresh
										void flags?.showFlag({
											type: 'error',
											title: intl.formatMessage(i18n.outdatedRefetchErrorFlagTitle),
											description: intl.formatMessage(i18n.outdatedRefetchErrorFlagDescription, {
												pageTitle: <b>{title}</b>,
											}),
											close: 'never',
											actions: [
												{
													content: intl.formatMessage(i18n.outdatedRefetchFlagRefresh),
													href: '',
													onClick: () => window.location.reload(),
												},
											],
										});
										isPublishLiveDocRedirectFailed = true;
									}
								} else {
									await forceRefetchContent();
								}

								// afterPublish should be responsible for triggering a router push in order to redirect users from edit page to view page.
								afterPublish({ res, newSpaceKey, isLivePagePublish: subType === 'live' });
							}
							if (editorOnboardingEligiblity && fg('cc_onboarding_experience')) {
								if (!hasUserPublished) {
									void setOnboardingState({
										key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
										value: 'true',
									});
								}

								// for the first publish, show either creator onboarding
								setHasMetPublishNudgeConditions(!hasUserPublished);
							}

							if (!schedulePublishDate && flags) {
								// when publishing the onboarding template for the first time
								// there will be separate publish flag shown through EditorPublishSuccessFlag
								const shouldShowTutorialSuccessFlag =
									isPublishingOriginalOnboardingTemplate && !editorTutorialSuccessFlagCompleted;

								const isValidAutomationDiscoveryUseCase =
									(fg('a4c_free_and_standard_editions') || edition === ConfluenceEdition.PREMIUM) &&
									contentType === 'page' &&
									isUnpublishedDraft;

								const eligibleForJiraLinkAutomationDiscoveryDialog =
									isValidAutomationDiscoveryUseCase &&
									!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_JIRA_LINK) &&
									doesADFContainJiraNodes(content);

								const eligibleForMeetingNotesAutomationDiscoveryDialog =
									isAdminHubAIEnabled &&
									isValidAutomationDiscoveryUseCase &&
									!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_MEETING_NOTES_TEMPLATE) &&
									title
										.toLowerCase()
										.includes(intl.formatMessage(i18n.titleKeywordForAutomationDiscovery));

								const shouldDisplayWhiteboardDiscoveryDialog =
									isValidAutomationDiscoveryUseCase &&
									!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_WHITE_BOARDS) &&
									isEligibleForWhiteboardDiscovery(title);

								const shouldDisplayReleaseNotesDiscoveryDialog =
									isValidAutomationDiscoveryUseCase &&
									!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_RELEASE_NOTES) &&
									isEligibleForReleaseNotesDiscovery(title, intl) &&
									fg('a4c_rovo_release_notes_prompt');

								const shouldDisplayProductRequirementsDiscoveryDialog =
									isValidAutomationDiscoveryUseCase &&
									!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_PRODUCT_REQUIREMENTS) &&
									isEligibleForProductRequirementsDiscovery(title, intl) &&
									fg('a4c_rovo_contextual_prompts');

								const shouldDisplayFeedbackAnalysisDiscoveryDialog =
									isValidAutomationDiscoveryUseCase &&
									!localStorage.getItemAsBoolean(keys.AUTOMATION_DISCOVER_FEEDBACK_ANALYSIS) &&
									isEligibleForFeedbackAnalysisDiscovery(title, intl) &&
									fg('a4c_rovo_feedback_analysis_prompt');

								const showModalProps = {
									publishedPageTitle: title,
									spaceName,
									spaceKey,
								};

								/**
								 * Replace the publish success flag w/ an automation dialog if:
								 * 1. It's a premium instance
								 * 2. The FF is enabled
								 * 3. The newly published page has the word "meeting" in it
								 * 4. Must be a page
								 * 5. User has not dismissed the dialog before (tracked in localStorage)
								 */

								/*
								 * If a Jira link is pasted AND the title has "meeting" in it, this triggers both automation dialogs.
								 * In that case, we will show the Jira link dialog and not the meeting notes dialog.
								 * Once the user dismisses/proceeds with the Jira link dialog, we will show the meeting notes dialog on next trigger.
								 */
								if (shouldDisplayWhiteboardDiscoveryDialog) {
									dialogs?.showModal(LazyWhiteBoardPublishDiscoveryDialog, showModalProps);
								} else if (shouldDisplayProductRequirementsDiscoveryDialog) {
									dialogs?.showModal(LazyProductRequirementsPublishDiscoveryDialog, showModalProps);
								} else if (eligibleForJiraLinkAutomationDiscoveryDialog) {
									experienceTracker.start({
										name: AUTOMATION_DISCOVERY_JIRA_LINK,
									});

									dialogs?.showModal(LazyJiraLinkAutomationDiscoveryDialog, showModalProps);
								} else if (shouldDisplayReleaseNotesDiscoveryDialog) {
									dialogs?.showModal(LazyReleaseNotesPublishDiscoveryDialog, showModalProps);
								} else if (shouldDisplayFeedbackAnalysisDiscoveryDialog) {
									dialogs?.showModal(LazyFeedbackAnalysisDiscoveryDialog, showModalProps);
								} else if (eligibleForMeetingNotesAutomationDiscoveryDialog) {
									experienceTracker.start({
										name: AUTOMATION_DISCOVERY_PUBLISH_MEETING_NOTES_EXPERIENCE,
									});

									dialogs?.showModal(LazyPagePublishAutomationDiscoveryDialog, showModalProps);
								} else {
									if (
										editorOnboardingEligiblity &&
										// conditions to hide the publish success flags
										(shouldShowTutorialSuccessFlag ||
											// first publish should show EditorPublishSuccessFlag instead
											!hasUserPublished) &&
										fg('cc_onboarding_experience')
									) {
										return;
									}

									// Publishing in the Company Hub involves more steps so the
									// success flag in that case comes later. These are
									// implemented in usePublishCompanyHub hook.
									// If we already showed the refetch error flag (isPublishLiveDocRedirectFailed=true), don't show this flag as it would confuse the user to see both.
									if (!isCompanyHub && !isPublishLiveDocRedirectFailed) {
										const isFirstTimePublish = isUnpublishedDraft || subType === 'live';
										const isConvertingFromLivePage =
											subType === 'live' && fg('confluence_live_pages_open_beta_trait_opted_in');

										const canShowPublishSuccessWithSocialAndCopyLink =
											isEligibleShareLinkSocialOnPublish &&
											isFirstTimePublish &&
											!isConvertingFromLivePage;

										if (canShowPublishSuccessWithSocialAndCopyLink) {
											void showPublishSuccessFlagsWithSocialAndCopyLink({
												spaceName,
												flags,
												title,
												intl,
												copyContentLink,
												createAnalyticsEvent,
												suggestedUsers,
												showShareModal,
											});
										} else {
											void showPublishSuccessFlags({
												isFirstTimePublish,
												spaceName,
												flags,
												title,
												intl,
												copyContentLink,
												createAnalyticsEvent,
												isConvertingFromLivePage,
											});
										}
									}

									// show sync block syncing failed error flag after the publish success flag as publishing was not blocked
									if (
										!flushSyncBlockSuccess &&
										expValEquals('platform_synced_block', 'isEnabled', true)
									) {
										setTimeout(() => {
											showFlushSyncBlockErrorFlag(
												flags,
												intl,
												editorPresetAPI?.syncedBlock?.actions.flushBodiedSyncBlocks,
											);
										}, 1000);
									}

									inviteOnLinkInsertEligibilityCheck(
										contentId,
										content,
										newVersionNumber,
										startingLinks,
									)
										.then((result) => {
											if (result) {
												void showInviteOnLinkInsertFlag(result);
											}
										})
										.catch(() => {
											// no-op
										});
								}
							}
						} else {
							showEditorFlag({
								errorName: responseMessage ?? '',
								statusCode: status ?? undefined,
								collabService: 'ncs',
								isUnpublishedDraft,
							});
							// prettier-ignore
							if (status === 400 && responseMessage?.includes("A page with this title already exists")) {
								// Don't fail experience for bad user input, e.g. duplicate title.
								// Confluence can throw BadRequestException 400 in other cases that may mean a code regression, although others may also be caused by bad user input
								experienceTracker.abort({
									name: experienceTrackingKey,
									reason: `${experienceTrackingKey} aborted: title is a duplicate`,
									attributes: {
										...defaultErrorAttributes,
										reason: responseMessage,
										traceId,
									},
								});
							} else if (status === 409 && (responseMessage?.includes("optimistic locking failed") || responseMessage?.includes("Version must be incremented on update"))) {
								// these errors are caused by user behaviour, e.g. simultaneous publishes by different users, or for HibernateOptimisticLockingFailureException ConflictException external updates to the content at the same time (e.g. comments, link refactoring)
								// therefore, we don't consider as a failure
								experienceTracker.abort({
									name: experienceTrackingKey,
									reason: `${experienceTrackingKey} aborted: content updated concurrently`,
									attributes: {
										...defaultErrorAttributes,
										reason: responseMessage,
										traceId,
									},
								});
							} else if (status && (status >= 400 && status <= 499)) {
								experienceTracker.abort({
									name: experienceTrackingKey,
									reason: `${experienceTrackingKey} aborted: User error with status code ${status}`,
									attributes: {
										...defaultErrorAttributes,
										traceId,
										reason: responseMessage,
									},
								});
							} else if (status && (status >= 500)) {
								experienceTracker.fail({
									name: experienceTrackingKey,
									error: new CollabPublishError(`${experienceTrackingKey} failed: Error with status code ${status}`, traceId),
									attributes: {
										...defaultErrorAttributes,
										traceId,
										reason: responseMessage,
									},
								});
							} else {
								// If the status code is not between 400 and 599, we consider it a client network failure
								experienceTracker.abort({
									name: experienceTrackingKey,
									reason: `${experienceTrackingKey} aborted: Presumed client network failure with status code ${status}`,
									attributes: {
										...defaultErrorAttributes,
										reason: responseMessage,
									},
								});
							}

							migratedRecently &&
								experienceTracker.fail({
									name: MIGRATE_PAGE_PUBLISH_EXPERIENCE,
									error: new Error(
										`${MIGRATE_PAGE_PUBLISH_EXPERIENCE} failed: Error with status code ${status}`,
									),
									attributes: {
										...defaultErrorAttributes,
										traceId,
									},
								});
						}
					});
				})
				.catch((err) => {
					const experienceError = err || new Error('unknown error while publishing content');

					// copied from packages/confluence-fabric-editor/src/components/ContentEditorBindings/ContentEditorBindingsComponent.tsx
					const errorAttributes = {
						...defaultErrorAttributes,
						stack: experienceError.stack,
					};

					if (err?.message == 'Called getResolvedEditorState before editorView is ready') {
						// editor has been unmounted, likely navigation during publish
						experienceTracker.abort({
							name: experienceTrackingKey,
							reason: `${experienceTrackingKey} aborted: Called getResolvedEditorState before editorView is ready`,
							attributes: defaultErrorAttributes,
						});
					} else if (
						containsNetworkError(err) ||
						err?.message === 'GraphQL error: Failed to fetch'
					) {
						void flags?.showErrorFlag({
							id: 'publish-network-error-flag',
							title: intl.formatMessage(i18n.networkErrorTitle),
							description: intl.formatMessage(i18n.networkErrorDescription),
						});

						experienceTracker.abort({
							name: experienceTrackingKey,
							reason: `${experienceTrackingKey} aborted: Client network failure`,
							attributes: defaultErrorAttributes,
						});
					} else {
						onCollabError();

						stopExperienceOnError(experienceTrackingKey, experienceError, errorAttributes);
					}

					migratedRecently &&
						stopExperienceOnError(
							MIGRATE_PAGE_PUBLISH_EXPERIENCE,
							experienceError,
							errorAttributes,
						);
				})
				.finally(() => {
					clearTimeout(publishTimeout);
					FABRIC_PUBLISH_PAGE_METRIC.cancel(); // cancel BM3 event for errors as errors may skew performance SLO

					// If user's live doc failed to redirect them on publish, don't set isPublishing to false because that will re-enable the editor
					if (!isPublishLiveDocRedirectFailed) {
						setIsPublishing(false);
						setIsPublishingContent(false);
					}
				});
		},
		[
			editorPresetAPI?.syncedBlock?.actions,
			checkIfConditionPreventingPublish,
			saveDraftPromise,
			setIsPublishing,
			refetchNativeCollab,
			contentId,
			collabEditInitialized,
			isPublishingContent,
			migratedRecently,
			getCollabProviderState,
			experienceTracker,
			isCompanyHub,
			showEditorFlag,
			editorOnboardingEligiblity,
			experimentalPublishEditorContentBlueprintMutation,
			contentType,
			spaceKey,
			showEditorModal,
			triggerPageConflictAnalytics,
			cloudId,
			afterPublish,
			flags,
			embeddedOnPublish,
			triggerAnalyticsEvent,
			createAnalyticsEvent,
			forceRefetchContent,
			forceRefetchPublishedLiveDoc,
			hasUserPublished,
			setHasMetPublishNudgeConditions,
			setOnboardingState,
			isPublishingOriginalOnboardingTemplate,
			editorTutorialSuccessFlagCompleted,
			edition,
			isAdminHubAIEnabled,
			intl,
			dialogs,
			inviteOnLinkInsertEligibilityCheck,
			showInviteOnLinkInsertFlag,
			startingLinks,
			copyContentLink,
			onCollabError,
			action,
			state,
			isLivePage,
			optimisticDeleteItemInTree,
			isEligibleShareLinkSocialOnPublish,
			showShareModal,
			suggestedUsers,
			publishPageMutation,
			publishBlogMutation,
			isAutoConverted,
		],
	);

	const onPublish = useCallback(
		({
			suppressNotifyWatchers = false,
			message = '',
			schedulePublishDate = null,
			ancestors = [],
			restrictions = undefined,
			retryOnConflict = true,
			newSpaceKey = undefined,
			moveRequest = undefined,
		}: PublishProps) => {
			return () =>
				handlePublish({
					suppressNotifyWatchers,
					message,
					retryOnConflict,
					isButtonClicked: true,
					schedulePublishDate,
					ancestors,
					restrictions,
					newSpaceKey,
					moveRequest,
				});
		},
		[handlePublish],
	);

	return useMemo(
		() => ({
			onPublish,
			isPublishingContent,
		}),
		[isPublishingContent, onPublish],
	);
};
